import { api } from 'api';
import cartApi from 'api/cart.api';

import { AppThunk } from 'store';
import {
  clearAllFulfillmentPreferences,
  setError,
  setIsLoading,
  setPromoDetails
} from 'store/reducers/cartSlice';

import { removeSpaces } from 'utils';
import {
  CayanFormData,
  cleanup,
  createFormElements,
  loadScript,
  RawFormData,
  validateElements
} from 'utils/cayanFormHelper';

import {
  CartItemDetailsParams,
  CheckoutCreationResponse,
  CheckoutParameters,
  CheckoutResponse,
  ExtendedPackageCartItemDetails,
  ShopCartType,
  ShopPromoParamsType
} from 'types';

export const getCartPackagesDetails =
  (
    paramsList: Omit<CartItemDetailsParams, 'item_type'>[]
  ): AppThunk<Promise<ExtendedPackageCartItemDetails[]>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const requests = paramsList.map(async (params) => {
        const { data } = await api.cart.getCartItemDetails({
          ...params,
          item_type: ShopCartType.Package
        });
        return {
          ...data,
          quantity: data.qry,
          id: params.item_id
        } as ExtendedPackageCartItemDetails;
      });
      return await Promise.all(requests);
    } catch (error) {
      console.error(`Error while getting cart item details: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      throw error;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const cartCompleteCheckout =
  (
    params: CheckoutParameters
  ): AppThunk<
    Promise<CheckoutResponse | CheckoutCreationResponse | undefined>
  > =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.cart.cartCompleteCheckout(params);
      const checkoutData = response?.data;
      if (checkoutData?.error || checkoutData?.sys_error) {
        const errorMessage =
          checkoutData.error || checkoutData.sys_error || 'Checkout failed';
        dispatch(setError(errorMessage));
        return undefined;
      }
      return checkoutData;
    } catch (error) {
      console.error(`Error while complete cart checkout: ${error.message}`);
      dispatch(setError(error.response?.data?.error_message || error.message));
      return undefined;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const validateShopPromoCode =
  (params: ShopPromoParamsType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.cart.validateShopPromoCode(params);

      if (data.success) {
        dispatch(setPromoDetails(data));
      } else {
        dispatch(setError(data.error ?? ''));
      }
    } catch (error) {
      console.error(`Error while validate promo code: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const cartExecutePayment =
  (
    invoiceId: number,
    onSuccessHandler: (id: number) => void
  ): AppThunk<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const response = await api.cart.cartExecutePayment(invoiceId);
      if (response?.data?.success) {
        // Clear fulfillment preferences after successful payment
        dispatch(clearAllFulfillmentPreferences());
        onSuccessHandler(
          response?.data?.confirmation_id ?? response?.data?.invoice_id
        );
      }
      return response?.data?.success;
    } catch (error) {
      console.error(
        `Error while executing order payment and completing the order: ${error.message}`
      );
      dispatch(setError(error.response?.data?.error_message || error.message));
      return undefined;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const cartExecuteOttPayment =
  (
    formData: RawFormData,
    onSuccessHandler: (_: string) => void = () => {}
  ): AppThunk<Promise<boolean | undefined>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));

      const prefsRes = await cartApi.verifyClCcardOttPrefs();
      const prefs = prefsRes.data;

      if (!prefs.script_url || !prefs.web_api_key) {
        dispatch(setError('Payment system error. Please contact support.'));
        return false;
      }

      await loadScript(prefs.script_url);
      window.CayanCheckout.setWebApiKey(prefs.web_api_key);

      const cayanFormData: CayanFormData = {
        cardnumber: removeSpaces(formData.card_number),
        expirationmonth: formData.exp_month.toString().padStart(2, '0'),
        expirationyear: formData.exp_year.toString(),
        cvv: formData.card_cvv2,
        cardholdername: formData.owner,
        street: formData.addr_ln1,
        city: formData.city,
        state: formData.state,
        zip: formData.zip
      };

      const elementsCreated = createFormElements(cayanFormData, {
        hidden: true,
        cleanup: true
      });

      if (!elementsCreated) {
        dispatch(setError('Failed to prepare payment form. Please try again.'));
        return false;
      }

      const validation = validateElements();
      if (!validation.isValid) {
        dispatch(setError('Payment form validation failed. Please try again.'));
        return false;
      }

      return await new Promise<boolean>((resolve) => {
        try {
          window.CayanCheckout.createPaymentToken({
            success: async (ottResult: { token: string }) => {
              try {
                await onSuccessHandler(ottResult.token);
                resolve(true);
              } catch {
                dispatch(setError('Payment failed. Please try again.'));
                resolve(false);
              }
            },
            error: (errors: { reason: string; message: string }[]) => {
              const errorMessage =
                errors && errors.length > 0
                  ? errors
                      .map((e) => e.reason || e.message || 'Unknown error')
                      .join(', ')
                  : 'Payment token creation failed';
              dispatch(setError(errorMessage));
              resolve(false);
            }
          });
        } catch {
          dispatch(setError('Payment system error. Please try again.'));
          resolve(false);
        }
      });
    } catch (error) {
      console.error(`Error while executing OTT payment: ${error.message}`);
      dispatch(setError('Payment error. Please try again.'));
      return false;
    } finally {
      dispatch(setIsLoading(false));
      cleanup();
    }
  };
